import React, { useState, useEffect, useCallback, useMemo } from 'react'
import { useNavigate } from 'react-router-dom'
import {
    Group,
    Text,
    Button,
    Modal,
    Stack,
    Textarea,
    Rating, Box,
    Divider
} from '@mantine/core'
import { IconStar, IconTrash } from '@tabler/icons-react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { storeRatingsApi, storesApi } from '../services/api'
import { useAuth } from '../hooks/useAuth'
import { useI18n } from '../hooks/useI18n'
import { CreateStoreRatingData, UpdateStoreRatingData } from '../types'

interface StoreRatingProps {
    storeId: string
    storeName: string
    averageRating?: number
    totalRatings?: number
    compact?: boolean
    showModal?: boolean
    onClose?: () => void
    onRateClick?: () => void
}

export const StoreRating: React.FC<StoreRatingProps> = ({
    storeId,
    storeName,
    averageRating = 0,
    totalRatings = 0,
    compact = false,
    showModal = true,
    onClose,
    onRateClick,
}) => {
    const navigate = useNavigate()
    const { t } = useI18n()
    const { isAuthenticated } = useAuth()
    const queryClient = useQueryClient()
    const [ratingModal, setRatingModal] = useState(false)
    const [rating, setRating] = useState(0)
    const [comment, setComment] = useState('')
    const [isEditing, setIsEditing] = useState(false)

    // Fetch user's existing rating
    const { data: myRating } = useQuery({
        queryKey: ['store-rating', storeId, 'my'],
        queryFn: () => storeRatingsApi.getMyStoreRating(storeId),
        enabled: isAuthenticated,
        select: (response) => response.data.data,
    })

    // Fetch store data to get updated ratings
    const { data: storeData } = useQuery({
        queryKey: ['store', storeId],
        queryFn: () => storesApi.getStore(storeId),
        select: (response) => response.data.data,
    })

    // Set initial values when myRating loads
    useEffect(() => {
        if (myRating) {
            setRating(myRating.rating)
            setComment(myRating.comment || '')
            setIsEditing(true)
        } else {
            setRating(0)
            setComment('')
            setIsEditing(false)
        }
    }, [myRating])

    // Create rating mutation
    const createRatingMutation = useMutation({
        mutationFn: (data: CreateStoreRatingData) => storeRatingsApi.createStoreRating(storeId, data),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['store-rating', storeId] })
            queryClient.invalidateQueries({ queryKey: ['store', storeId] })
            queryClient.invalidateQueries({ queryKey: ['stores'] })
            closeModal()
        },
    })

    // Update rating mutation
    const updateRatingMutation = useMutation({
        mutationFn: (data: UpdateStoreRatingData) => storeRatingsApi.updateStoreRating(storeId, data),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['store-rating', storeId] })
            queryClient.invalidateQueries({ queryKey: ['store', storeId] })
            queryClient.invalidateQueries({ queryKey: ['stores'] })
            closeModal()
        },
    })

    // Delete rating mutation
    const deleteRatingMutation = useMutation({
        mutationFn: () => storeRatingsApi.deleteStoreRating(storeId),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['store-rating', storeId] })
            queryClient.invalidateQueries({ queryKey: ['store', storeId] })
            queryClient.invalidateQueries({ queryKey: ['stores'] })
            setRating(0)
            setComment('')
            setIsEditing(false)
            closeModal()
        },
    })

    const handleSubmit = useCallback(() => {
        if (rating === 0) return

        const data = {
            rating,
            comment: comment.trim() || undefined,
        }

        if (isEditing) {
            updateRatingMutation.mutate(data)
        } else {
            createRatingMutation.mutate(data)
        }
    }, [rating, comment, isEditing, updateRatingMutation, createRatingMutation])

    const handleDelete = useCallback(() => {
        deleteRatingMutation.mutate()
    }, [deleteRatingMutation])

    const closeModal = useCallback(() => {
        setRatingModal(false)
        // Reset to current values if editing
        if (myRating) {
            setRating(myRating.rating)
            setComment(myRating.comment || '')
        } else {
            setRating(0)
            setComment('')
        }
        // Call parent onClose if provided
        if (onClose) {
            onClose()
        }
    }, [myRating, onClose])

    const handleCommentChange = useCallback((e: React.ChangeEvent<HTMLTextAreaElement>) => {
        setComment(e.currentTarget.value)
    }, [])

    const handleRatingChange = useCallback((value: number) => {
        setRating(value)
    }, [])

    const isLoading = createRatingMutation.isPending || updateRatingMutation.isPending || deleteRatingMutation.isPending

    const RatingContent = useMemo(() => (
        <Stack gap="md">
            <Text size="sm" c="dimmed">
                {t('store.rating.rateDescription', { storeName })}
            </Text>

            <Box>
                <Text size="sm" fw={500} mb="xs">
                    {t('store.rating.rating')} *
                </Text>
                <Rating
                    value={rating}
                    onChange={handleRatingChange}
                    size="lg"
                />
            </Box>

            <Box>
                <Text size="sm" fw={500} mb="xs">
                    {t('store.rating.comment')} ({t('store.rating.optional')})
                </Text>
                <Textarea
                    value={comment}
                    onChange={handleCommentChange}
                    placeholder={t('store.rating.commentPlaceholder')}
                    rows={3}
                    maxLength={1000}
                />
                <Text size="xs" c="dimmed" ta="right" mt="xs">
                    {comment.length}/1000
                </Text>
            </Box>

            <Divider />

            <Group justify="space-between">
                <Group>
                    {isEditing && (
                        <Button
                            variant="outline"
                            color="red"
                            size="sm"
                            onClick={handleDelete}
                            loading={deleteRatingMutation.isPending}
                            leftSection={<IconTrash size={14} />}
                        >
                            {t('store.rating.delete')}
                        </Button>
                    )}
                </Group>

                <Group>
                    <Button variant="outline" onClick={closeModal}>
                        {t('common.cancel')}
                    </Button>
                    <Button
                        onClick={handleSubmit}
                        loading={isLoading}
                        disabled={rating === 0}
                    >
                        {isEditing ? t('common.update') : t('common.submit')}
                    </Button>
                </Group>
            </Group>
        </Stack>
    ), [
        t, storeName, rating, comment, isEditing, isLoading,
        handleRatingChange, handleCommentChange, handleDelete, handleSubmit, closeModal,
        deleteRatingMutation.isPending
    ])

    const ratingButtonClicked = useCallback(() => {
        if (isAuthenticated) {
            if (onRateClick) {
                onRateClick()
            } else {
                setRatingModal(true)
            }
        } else {
            navigate('/login')
        }
    }, [isAuthenticated, onRateClick, navigate])

    if (compact) {
        return (
            <Group gap="xs">
                <Text
                    size="xs"
                    style={{
                        cursor: 'pointer',
                        color: 'var(--mantine-color-customGreen-6)'
                    }}
                    onClick={ratingButtonClicked}
                    onMouseEnter={(e) => {
                        e.currentTarget.style.textDecoration = 'underline'
                    }}
                    onMouseLeave={(e) => {
                        e.currentTarget.style.textDecoration = 'none'
                    }}
                >
                    {(storeData?.rating?.averageRating || averageRating) > 0 ? (
                        <>
                            <IconStar size={14} fill="gold" color="gold" style={{ marginRight: 4, verticalAlign: 'middle', marginBottom: 2 }} />
                            {(storeData?.rating?.averageRating || averageRating).toFixed(1)} ({storeData?.rating?.totalRatings || totalRatings})
                        </>
                    ) : (
                        t('store.rating.rate')
                    )}
                </Text>
            </Group>
        )
    }

    // If showModal is false, we're being used inside another modal, so render content directly
    if (!showModal) {
        return RatingContent
    }

    // Otherwise, render our own modal
    return (
        <Modal
            opened={ratingModal}
            onClose={closeModal}
            title={isEditing ? t('store.rating.editTitle') : t('store.rating.rateTitle', { storeName })}
            size="md"
        >
            {RatingContent}
        </Modal>
    )
} 